import { useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Download } from "lucide-react";
import { Button } from "@/components/ds";
import { useToast } from "@/hooks/use-toast";

/**
 * C-QR (JL-E17): level M, a 4-module white border, black on white — never brand-coloured or
 * themed. These two colour literals are the only ones in the feature, on purpose: a printed
 * QR must scan the same in light and dark mode.
 */
export const JOIN_QR_SPEC = {
  level: "M",
  marginSize: 4,
  fgColor: "#000000",
  bgColor: "#FFFFFF",
  pngSize: 1024,
} as const;

/** Drawn into every test-link file (JL-E13) so a Dev QR is never printed by mistake. */
export const JOIN_QR_TEST_LABEL = "TEST — not for employees";

/** Test-link PNG layout: the QR in the 896-px top area, centred; the label in the bottom 128-px band. */
const TEST_QR_PX = 896;
/** Test-link SVG: the label band is the same share of the code as in the PNG (128 / 896). */
const TEST_BAND_SHARE = (JOIN_QR_SPEC.pngSize - TEST_QR_PX) / TEST_QR_PX;
const SVG_NS = "http://www.w3.org/2000/svg";
const LABEL_FONT = "Geist, Arial, Helvetica, sans-serif";

/** `athlyn-join-<subdomain>.<ext>`, or `TEST-<subdomain>-join.<ext>` for a test link. */
export function joinQrFileName(subdomain: string, isTestLink: boolean, ext: "png" | "svg"): string {
  const slug = subdomain.trim().toLowerCase() || "link";
  return isTestLink ? `TEST-${slug}-join.${ext}` : `athlyn-join-${slug}.${ext}`;
}

const round = (value: number) => String(Math.round(value * 1000) / 1000);

/**
 * The on-screen QR as a standalone SVG file: a copy (the element on screen is untouched),
 * screen-only attributes dropped, 1024 wide, one `xmlns`, white-backed over the whole viewBox;
 * on a test link the viewBox grows downward and the TEST label is drawn, black on white, below the code.
 */
export function buildJoinQrSvg(source: SVGSVGElement, isTestLink: boolean): string {
  const svg = source.cloneNode(true) as SVGSVGElement;
  for (const name of ["class", "style", "role", "aria-label", "aria-hidden", "data-testid"]) svg.removeAttribute(name);

  const box = (svg.getAttribute("viewBox") ?? "").trim().split(/\s+/).map(Number);
  const size = box.length === 4 && box[2] > 0 ? box[2] : 0;
  const px = JOIN_QR_SPEC.pngSize;

  const height = isTestLink && size > 0 ? size + size * TEST_BAND_SHARE : size;
  if (size > 0) {
    // One white rect behind EVERYTHING (code + label band): a dark viewer must never show
    // through, or the black TEST label would be unreadable.
    const background = document.createElementNS(SVG_NS, "rect");
    background.setAttribute("x", "0");
    background.setAttribute("y", "0");
    background.setAttribute("width", round(size));
    background.setAttribute("height", round(height));
    background.setAttribute("fill", JOIN_QR_SPEC.bgColor);
    svg.insertBefore(background, svg.firstChild);
  }

  if (isTestLink && size > 0) {
    const band = height - size;
    svg.setAttribute("viewBox", `0 0 ${round(size)} ${round(height)}`);
    const text = document.createElementNS(SVG_NS, "text");
    text.setAttribute("x", round(size / 2));
    text.setAttribute("y", round(size + band / 2));
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("dominant-baseline", "central");
    text.setAttribute("font-family", LABEL_FONT);
    text.setAttribute("font-weight", "600");
    text.setAttribute("font-size", round(band * 0.34));
    text.setAttribute("fill", JOIN_QR_SPEC.fgColor);
    text.textContent = JOIN_QR_TEST_LABEL;
    svg.append(text);
    svg.setAttribute("width", String(px));
    svg.setAttribute("height", String(Math.round((px * height) / size)));
  } else {
    svg.setAttribute("width", String(px));
    svg.setAttribute("height", String(px));
  }

  let markup = new XMLSerializer().serializeToString(svg);
  const openTag = markup.slice(0, markup.indexOf(">"));
  if (!openTag.includes(`xmlns="${SVG_NS}"`)) markup = markup.replace("<svg", `<svg xmlns="${SVG_NS}"`);
  return `<?xml version="1.0" encoding="UTF-8"?>\n${markup}\n`;
}

/** One horizontal run of dark modules: `w` modules from (`x`, `y`), in module units incl. the quiet zone. */
export interface QrRun {
  x: number;
  y: number;
  w: number;
}

// qrcode.react writes each run as `M{x} {y}h{w}v1H{x}z` (or `M{x},{y} h{w}v1H{x}z` at a row's end).
const RUN = /M(\d+)[ ,](\d+)\s*h(\d+)v1H\d+z/g;

/**
 * The module matrix of the on-screen `QRCodeSVG`, read back from its dark path: the module
 * count `size` (code + quiet zone, from the viewBox) and the dark runs. Lets the PNG be drawn
 * at exact integer module sizes — no hidden canvas, nothing scaled by devicePixelRatio.
 */
export function qrRunsFromSvg(svg: SVGSVGElement): { size: number; runs: QrRun[] } {
  const box = (svg.getAttribute("viewBox") ?? "").trim().split(/\s+/).map(Number);
  const size = box.length === 4 && box[2] > 0 ? box[2] : 0;
  const dark = Array.from(svg.querySelectorAll("path")).find(
    (path) => path.getAttribute("fill")?.toUpperCase() === JOIN_QR_SPEC.fgColor
  );
  const d = dark?.getAttribute("d") ?? "";
  const runs = Array.from(d.matchAll(RUN), (m) => ({
    x: Number(m[1]),
    y: Number(m[2]),
    w: Number(m[3]),
  }));
  // Every `M` must be a run we understood; otherwise the matrix is unknown — return none.
  const commands = (d.match(/M/g) ?? []).length;
  return { size, runs: commands === runs.length ? runs : [] };
}

function saveAs(href: string, fileName: string) {
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

interface JoinLinkQrProps {
  /** The server's `joinUrl` — the QR encodes exactly this, never `window.location`. */
  joinUrl: string;
  subdomain: string;
  isTestLink: boolean;
  /** False when the company's app isn't in the stores: no QR, no downloads (JL-D20). */
  available: boolean;
}

/** The company's QR (mock-up 2.3): on a white tile, PNG (screens · email) and SVG (print) downloads. */
export default function JoinLinkQr({ joinUrl, subdomain, isTestLink, available }: JoinLinkQrProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { toast } = useToast();

  if (!available) {
    return (
      <p className="text-[13px] text-ds-ink2" data-testid="join-link-qr-unavailable">
        No app for this company yet — the link shows “This app link isn't ready yet”.
      </p>
    );
  }

  const { pngSize, level, marginSize, fgColor, bgColor } = JOIN_QR_SPEC;
  const qr = { value: joinUrl, level, marginSize, fgColor, bgColor };

  const downloadPng = () => {
    const source = svgRef.current;
    if (!source) return;
    const { size, runs } = qrRunsFromSvg(source);
    // Never save a blank (or label-only) PNG that looks plausible.
    if (size === 0 || runs.length === 0) {
      toast({ title: "Couldn't make the PNG", variant: "destructive" });
      return;
    }
    const out = document.createElement("canvas");
    out.width = pngSize;
    out.height = pngSize;
    const ctx = out.getContext("2d");
    if (!ctx) return;
    // The QR area: the whole square, or the 896-px top area on a test link (label band below).
    const area = isTestLink ? TEST_QR_PX : pngSize;
    // Equal whole-pixel modules, centred in the area; any spare pixels widen the white border.
    const module = Math.floor(area / size);
    const drawn = module * size;
    const left = Math.floor((pngSize - drawn) / 2);
    const top = Math.floor((area - drawn) / 2);
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, pngSize, pngSize);
    ctx.fillStyle = fgColor;
    for (const run of runs) ctx.fillRect(left + run.x * module, top + run.y * module, run.w * module, module);
    if (isTestLink) {
      ctx.font = `600 44px ${LABEL_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(JOIN_QR_TEST_LABEL, pngSize / 2, TEST_QR_PX + (pngSize - TEST_QR_PX) / 2);
    }
    saveAs(out.toDataURL("image/png"), joinQrFileName(subdomain, isTestLink, "png"));
  };

  const downloadSvg = () => {
    const source = svgRef.current;
    if (!source) return;
    const blob = new Blob([buildJoinQrSvg(source, isTestLink)], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    saveAs(url, joinQrFileName(subdomain, isTestLink, "svg"));
    // After the click has handed the file to the browser.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <div className="flex flex-wrap items-start gap-3.5" data-testid="join-link-qr">
      <div
        className="flex-none overflow-hidden rounded-ds border border-ds-line"
        style={{ background: bgColor }}
        role="img"
        aria-label={`QR code for ${joinUrl}`}
      >
        <QRCodeSVG ref={svgRef} {...qr} size={112} className="block" aria-hidden="true" />
      </div>
      <div className="flex min-w-0 flex-col items-start gap-2">
        <Button variant="secondary" size="sm" icon={<Download className="h-3.5 w-3.5" aria-hidden="true" />} onClick={downloadPng}>
          Download PNG <small className="font-normal text-ds-ink3">screens · email</small>
        </Button>
        <Button variant="secondary" size="sm" icon={<Download className="h-3.5 w-3.5" aria-hidden="true" />} onClick={downloadSvg}>
          Download SVG <small className="font-normal text-ds-ink3">print</small>
        </Button>
        <p className="break-all font-ds-mono text-xs text-ds-ink3">
          {joinQrFileName(subdomain, isTestLink, "png")} / .svg
        </p>
      </div>
    </div>
  );
}
